import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  BasicTable,
  CardComponent,
  OverviewCard,
  PageTitleAndSubTitle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import React, { useEffect, useState } from 'react';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import DateFilter from '../../_components/molecules/DateFilter';
import {
  CategoryData,
  CategoryList,
  formatCardData,
  formatCategoryData,
  generateCategoryList,
  getBenchmarkCategoriesColumn,
  SummaryData,
} from '../../core-utils/helper-utils/benchmarkHelper';
import { scrollBarStyles } from '../../core-utils/messages/CommonConstants';
import { benchmarkConstants } from '../../core-utils/messages/benchmarkConstants';
import { observer } from 'mobx-react';
import {
  getCategories,
  getSummary,
} from '../../core-utils/axios/benchmark/benchmark';
import { useStores } from '../../stores';
import usePlanDates from '../../core-utils/custom-hooks/PlanDates';
import { useAPIPayload } from '../../core-utils/helper-utils/apiPayloadHelper';
import useSnackbar from '../../core-utils/custom-hooks/Snackbar';
import Snackbar from '../../_components/atoms/Snackbar';
import Loader from '../../_components/molecules/Loader';
import { useNavigate } from 'react-router-dom';
import { CMS_BENCHMARK_INTERNAL } from '../../core-utils/routes';
import useTotalEligible from '../../core-utils/custom-hooks/TotalEligible';
import { getCohortValue } from '../../core-utils/helper-utils/overviewCardHelper';
import { numFormatterWithSuffix } from '../../core-utils/helper-utils/helper';
import CohortDetailsModal from '../../_components/organisms/CohortDetailsModalNew';
import { useCohortModal } from '../../core-utils/custom-hooks/useCohortModal';
import {
  checkUserAccess,
  UserAccessConstants,
} from '../../core-utils/helper-utils/userAccess';
import CohortFilterBar from '../../_components/molecules/CohortFilterBar';
import { useCohortOnPageLoad } from '../../core-utils/custom-hooks/useCohortPageOnLoad';
import { getCohortMembersCount } from '../../core-utils/axios/Cohort/Summary';

const useStyles = makeStyles()({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '1230px',
    padding: '40px 0px 66px 63px',
  },
  hr: {
    width: '100%',
    border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    margin: '18px 0px',
  },
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  overviewCardContainer: {
    width: '100%',
    display: 'flex',
    gap: '1rem',
  },
  flex: {
    display: 'flex',
  },
  tableGrid: {
    overflowX: 'auto',
    width: '1130px',
    ...scrollBarStyles,
  },
});

const BenchmarkPage = observer(() => {
  const { classes } = useStyles();

  const overviewCardStyles = {
    rootStyles: {
      height: 'auto',
      padding: '1rem',
    },
  };

  const messages = benchmarkConstants;
  const {
    employerGroupStore,
    quaterlyReportStore,
    loginPageStore,
    cohortDetailsStore,
  } = useStores();

  const {
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    isIncurredDate,
    isPaidDate,
    isPlanDatesFetched,
    aggregationParameter,
  } = quaterlyReportStore;

  const { getPlanDates, loading: isPlanDatesLoading } = usePlanDates();
  const payload = useAPIPayload();
  const { snackbarState, handleCloseSnackBar, openSnackbar } = useSnackbar();
  const { open, openModal, closeModal, isEditMode } = useCohortModal();
  const navigate = useNavigate();

  const { totalResults, isInitializing } = useCohortOnPageLoad({
    fetchTotalResults: () =>
      getCohortMembersCount(
        employerGroupStore.employerGroupResponseData.eg_nid,
        aggregationParameter === 'paid' ? paidEndDate : incurredEndDate,
        employerGroupStore.employerGroupResponseData.max_claim_date,
      ),
    enabled:
      isPlanDatesFetched &&
      !!employerGroupStore?.employerGroupResponseData?.eg_nid?.[0],
  });

  const { overallTotalCost, getTotalEligible } = useTotalEligible(
    aggregationParameter === 'paid' ? paidStartDate : incurredStartDate,
    aggregationParameter === 'paid' ? paidEndDate : incurredEndDate,
    employerGroupStore,
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [summaryData, setSummaryData] = useState<SummaryData>({
    benchmarked_spend: 0,
    avgRatevsCMS: 0,
    percent_benchmarked_spend_gt_150_CMS: 0,
  });
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [categoryList, setCategoryList] = useState<CategoryList[]>([]);

  const handleCategoryClick = (
    categoryKey: string,
    categoryDescription: string,
  ) => {
    navigate(CMS_BENCHMARK_INTERNAL, {
      state: {
        categoryKey,
        categoryList,
        categoryDescription,
      },
    });
  };

  const OverviewCards = () => {
    return (
      <Grid className={classes.overviewCardContainer}>
        <OverviewCard
          title={messages.CARDS.CARD1_TITLE}
          value={getCohortValue(
            `$${numFormatterWithSuffix(summaryData?.benchmarked_spend)}`,
            overallTotalCost.totalCost,
          )}
          rootStyles={overviewCardStyles.rootStyles}
          isIcon
          tooltipText={messages.COLUMNS.BENCHMARK_SPEND.description}
        />
        <OverviewCard
          title={messages.CARDS.CARD2_TITLE}
          value={formatCardData(summaryData?.avgRatevsCMS, 'percentage')}
          rootStyles={overviewCardStyles.rootStyles}
          isIcon
          tooltipText={messages.COLUMNS.AVG_RATE_VS_CMS_WEIGHTED.description}
        />
        <OverviewCard
          title={messages.CARDS.CARD3_TITLE}
          value={formatCardData(
            summaryData?.percent_benchmarked_spend_gt_150_CMS,
            'percentage',
          )}
          rootStyles={overviewCardStyles.rootStyles}
          isIcon
          tooltipText={messages.COLUMNS.PERCENT_SPEND_OVER_150_CMS.description}
        />
      </Grid>
    );
  };
  const TableCard = () => {
    return (
      <CardComponent
        title={messages.TABLE}
        titleSubTitleMargin="1rem"
        body={
          <Grid className={classes.tableGrid}>
            <Grid
              style={{
                width: '1130px',
                // getBenchmarkCategoriesColumn().length > 4
                //   ? `${getBenchmarkCategoriesColumn().length * 200}px`
                //   : '1130px',
                paddingBottom: '0.5rem',
              }}
            >
              <BasicTable
                columns={getBenchmarkCategoriesColumn()}
                rowsData={formatCategoryData(categories, handleCategoryClick)}
                displayType="box"
                isFullTable
                headerCellHeight="46px"
                headerColor={patientAccessTheme.palette.grey[700]}
                headerTextColor={patientAccessTheme.palette.status.white}
                cellHeight="46px"
                cellPadding="12px 9px 12px 14px"
              />
            </Grid>
          </Grid>
        }
      />
    );
  };

  const apiCalls = async () => {
    setLoading(true);
    try {
      if (isPlanDatesFetched) {
        const responses = await Promise.allSettled([
          getTotalEligible(),
          getSummary(payload, cohortDetailsStore.memberUniqueIdList),
          getCategories(payload, cohortDetailsStore.memberUniqueIdList),
        ]);

        if (responses[1].status === 'fulfilled') {
          setSummaryData(responses[1].value.data?.data);
        }
        if (responses[2].status === 'fulfilled') {
          setCategories(responses[2].value.data?.data);
          setCategoryList(generateCategoryList(responses[2].value.data?.data));
        }
      }
    } catch (error) {
      openSnackbar(messages.ERROR, 'error', 2000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!employerGroupStore?.employerGroupResponseData?.eg_nid[0]) {
      return;
    }
    const fetchPlanDates = async () => {
      if (!isPlanDatesFetched) {
        await getPlanDates();
      }
    };

    fetchPlanDates();
  }, [employerGroupStore?.employerGroupResponseData?.eg_nid]);

  useEffect(() => {
    if (isInitializing) {
      return;
    }

    apiCalls();
  }, [
    isInitializing,
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    isIncurredDate,
    isPaidDate,
    isPlanDatesFetched,
    aggregationParameter,
    cohortDetailsStore.conditionsDetails,
  ]);

  const isPageLoading = isPlanDatesLoading || loading || isInitializing;

  return isPageLoading ? (
    <Loader />
  ) : (
    <>
      <Grid className={classes.root}>
        <PageTitleAndSubTitle
          title={messages.TITLE}
          subTitle={messages.SUBTITLE}
          chip={
            <Grid className={classes.flex}>
              <Typography
                fontSize="17px"
                lineHeight="21px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                fontType="semiBold"
                margin="2px 8px 0px 0px"
              >
                for
              </Typography>
              <DateFilter />
            </Grid>
          }
          showQueryDataButton={checkUserAccess(
            UserAccessConstants.COHORT_DETAILS,
            loginPageStore.userResponse.role.name,
            loginPageStore.userFeatures,
          )}
          queryButtonText={
            cohortDetailsStore.cohortDetailsList?.length > 0 &&
            cohortDetailsStore.cohortDetailsList[0].category !== ''
              ? 'Edit Cohort'
              : 'Add Cohort'
          }
          queryDataAction={() => {
            if (
              checkUserAccess(
                UserAccessConstants.COHORT_DETAILS,
                loginPageStore.userResponse.role.name,
                loginPageStore.userFeatures,
              )
            ) {
              openModal();
            }
          }}
        />
        <hr className={classes.hr} />
        <Grid className={classes.mainContainer}>
          <CohortFilterBar />
          <OverviewCards />
          <TableCard />
        </Grid>
      </Grid>

      <CohortDetailsModal
        open={open}
        handleClose={closeModal}
        totalResults={totalResults}
        isEditMode={isEditMode}
      />

      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </>
  );
});

export default BenchmarkPage;
